import { extendFormToMap, selectBasketCheckPriceItem, selectBasketItem, selectBasketToPay, selectBasketWindowError, updateBasketItem } from 'containers/OrderDetails/basketSlice'
import {
  RentalWindow, ShortTermCalendarData, ShortTermConfig, WindowError,
  estimatePrice, fitDuration, formatWall, getDefaultDuration, getItemDuration, getSlotStep, parseBusy, parseWall, roundUp,
  wallDayLabel, wallTime
} from 'utils/shortTerm'
import { useReduxDispatch, useReduxSelector } from 'store'
import RentalWindowSummary from './RentalWindowSummary'
import RentalWindowPicker from './RentalWindowPicker'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import React, { useLayoutEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import './style.scss'

/**
 * Wybór okna najmu krótkoterminowego - zamiast miesięcznego kalendarza,
 * gdy obiekt w checkPrice ma blok shortTerm. Okno trzymamy w pozycji koszyka jako startDateTime + durationMinutes
 * (czas ścienny placu); każda zmiana pozycji wysyła koszyk (Reservation), który zwraca rentalWindow z ceną.
 */
const ShortTermCalendar: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const item = useReduxSelector(state => selectBasketItem(state, id)) as basic.Form
  const checkPrice = useReduxSelector(state => selectBasketCheckPriceItem(state, id)) as basic.Form
  const windowError = useReduxSelector(state => selectBasketWindowError(state, id)) as WindowError | undefined
  const toPay = useReduxSelector(selectBasketToPay) as basic.Form | undefined

  const dispatch = useReduxDispatch()
  const { t, lang } = useTranslation('calendar.shortTerm')

  useLayoutEffect(() => { dispatch(extendFormToMap(false)) }, [dispatch])

  const shortTerm = checkPrice.shortTerm as ShortTermConfig
  const calendar = checkPrice.calendar as ShortTermCalendarData | undefined
  const ownerNote = checkPrice.ownerNote as string | undefined
  const busy = useMemo(() => parseBusy(calendar?.busy), [calendar?.busy])

  const unit = Math.max(1, Number(shortTerm.unitMinutes) || 30)
  const start = parseWall(item.startDateTime)
  const duration = getItemDuration(item) || getDefaultDuration(shortTerm)

  const save = (startDateTime: string, durationMinutes: number) => {
    dispatch(updateBasketItem({
      identifier: id,
      values: { ok: true, startDateTime, durationMinutes, endDateTime: undefined }
    }))
  }

  // cena: z koszyka (źródło prawdy), dopóki odpowiedź dotyczy aktualnego okna; inaczej podgląd z siatki cennika
  const position = toPay?.[id] as basic.Form | undefined
  const rentalWindow = position?.rentalWindow as RentalWindow | undefined
  const isConfirmed = !!rentalWindow && start !== undefined
    && parseWall(rentalWindow.start) === start
    && Number(rentalWindow.minutes) === roundUp(duration, unit)
  const pricing = isConfirmed && rentalWindow?.pricing ? rentalWindow.pricing : estimatePrice(shortTerm, duration)
  const notice = isConfirmed ? (rentalWindow?.notice || position?.rentalWindowNotice) : undefined

  const nextAvailable = parseWall(windowError?.availability?.nextAvailableFrom)
  const fullDate = (m: number) => `${wallDayLabel(m, lang, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}, ${wallTime(m)}`

  return (
    <div className='calendar shortTermCalendar'>
      <div className='calendar__main'>
        {ownerNote && <div className='ownershipInfo'>
          <p dangerouslySetInnerHTML={{ __html: ownerNote }} />
        </div>}

        <div className='title'>
          <div className='group'>
            <span>{t('title')}</span>
          </div>
        </div>

        <RentalWindowPicker
          shortTerm={shortTerm}
          calendar={calendar}
          startDateTime={item.startDateTime}
          durationMinutes={duration}
          onChange={save}
        />

        {windowError && <div className='shortTermCalendar__error'>
          <div className='shortTermCalendar__error__title'>{t('unavailableTitle')}</div>
          {windowError.errors.map((error, idx) => <div key={idx}>{error}</div>)}
          {nextAvailable !== undefined && <>
            <div>{t('nearestAvailable').replace('{date}', fullDate(nextAvailable))}</div>
            <ArrowBtn
              showArrow={false}
              className='--secondary'
              text={t('useNearest')}
              action={() => {
                const s = roundUp(nextAvailable, getSlotStep(shortTerm))
                save(formatWall(s), fitDuration(shortTerm, busy, s, duration))
              }}
            />
          </>}
        </div>}

        <RentalWindowSummary
          startDateTime={item.startDateTime}
          durationMinutes={duration}
          pricing={pricing}
          isConfirmed={isConfirmed}
          notice={notice}
        />

        <div className='calendar__info'>
          <div>{t('paymentInfo')}</div>
        </div>
      </div>
    </div>
  )
}

export default ShortTermCalendar
